// Shared deviation ("avvikelse") math, mirrored by the backend SetpointController.
//
// Deviation per value: (value - target) / |target| * 100.
// When target === 0 a percentage is undefined, so we return absolute differences
// and set devNull = true (the UI then drops the "%" suffix).

export interface DeviationResult {
    /** Number of values used. */
    count: number;
    avg: number | null;
    /** Deviation of the average. % unless devNull, then absolute difference. */
    devAvg: number | null;
    /** True when target === 0 → dev* are absolute differences, not percentages. */
    devNull: boolean;
}

const EMPTY: DeviationResult = {
    count: 0, avg: null, devAvg: null, devNull: false,
};

export function computeDeviation(values: Array<number | null | undefined>, target: number): DeviationResult {
    const nums = values.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
    if (nums.length === 0) return { ...EMPTY };

    const sum = nums.reduce((a, b) => a + b, 0);
    const avg = sum / nums.length;

    const denom = Math.abs(target);
    const devNull = denom === 0;
    const dev = (x: number): number => (devNull ? x - target : ((x - target) / denom) * 100);

    return {
        count: nums.length,
        avg,
        devAvg: dev(avg),
        devNull,
    };
}

/** Format a deviation value for display, e.g. "+12.3 %" or "-2.0" (absolute when devNull). */
export function formatDeviation(value: number | null, devNull: boolean): string {
    if (value === null || value === undefined || Number.isNaN(value)) return 'n/a';
    const sign = value > 0 ? '+' : '';
    return devNull ? `${sign}${value.toFixed(1)}` : `${sign}${value.toFixed(1)} %`;
}
